class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems = this.closest("cart-items");
      if (cartItems) {
        const itemIndex = this.dataset.index;
        cartItems.updateQuantity(itemIndex, 0, event);
      }
    });
  }
}
customElements.define("cart-remove-button", CartRemoveButton);

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
    this.querySelector("#CartDrawer-Overlay")?.addEventListener("click", this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    if (this.hasInitialized) return;
    this.hasInitialized = true;

    // Quantity button clicks
    this.addEventListener(
      "click",
      (this._boundClickHandler ??= (event) => {
        const button = event.target.closest(".quantity__button");
        if (button) this.handleQuantityButtonClick(event, button);
      })
    );

    // Prevent Shopify quantity input "change" from firing another update
    this.querySelectorAll(".quantity__input").forEach((input) => {
      input.addEventListener("change", (e) => e.stopImmediatePropagation(), true);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");

    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.setActiveElement(cartLink);
      this.open(cartLink);
    });

    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.setActiveElement(cartLink);
        this.open(cartLink);
      }
    });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  open() {
    if (!this.classList.contains("active")) {
      this.classList.add("animate", "active");
    }

    const focusElement = this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
    const containerToTrapFocusOn = this.classList.contains("is-empty")
      ? this.querySelector(".drawer__inner-empty")
      : document.getElementById("CartDrawer");
    trapFocus(containerToTrapFocusOn, focusElement);

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active", "animate");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");

    // Restore focus to the element that triggered the drawer
    if (this.activeElement) this.activeElement.focus();
  }

  getSectionsToRender() {
    return [
      { id: "CartDrawer", section: "cart-drawer", selector: ".drawer__inner" },
      { id: "cart-icon-bubble", section: "cart-icon-bubble", selector: ".cart-icon-bubble" },
      { id: "cart-live-region-text", section: "cart-live-region-text", selector: ".cart-live-region-text" },
    ];
  }

  getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    if (!selector) return html;
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : html;
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (!drawerInner) {
      console.error('Drawer inner element not found');
      return;
    }

    // Remove empty state
    drawerInner.classList.remove('is-empty');

    // Update sections with new HTML
    this.getSectionsToRender().forEach((section) => {
      const container = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!container) {
        console.warn(`Container not found for section: ${section.id}`);
        return;
      }

      const newHtml = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      if (newHtml) {
        container.innerHTML = newHtml;
      } else {
        console.warn(`No HTML returned for section: ${section.section}`);
      }
    });

    // Reattach overlay click listener
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.removeEventListener('click', this.close);
      overlay.addEventListener('click', this.close.bind(this));
    }

    // Ensure the drawer is closed before updating to avoid visual glitches
    this.classList.remove('animate', 'active');

    // Force DOM reflow
    void this.offsetWidth;

    // Open drawer with updated content
    setTimeout(() => {
      this.open();
    }, 0); // Use minimal delay to ensure DOM updates are applied
  }


  refreshSections(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const container = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!container) return;

      const newHtml = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      if (newHtml) container.innerHTML = newHtml;
    });

    // Open drawer after refresh
    setTimeout(() => {
      const overlay = this.querySelector("#CartDrawer-Overlay");
      if (overlay) {
        overlay.removeEventListener("click", this.close);
        overlay.addEventListener("click", this.close.bind(this));
      }
      this.classList.remove("animate", "active");
      void this.offsetWidth;
      this.open();
    }, 50);
  }

  handleQuantityButtonClick(event, button) {
    event.preventDefault();
    event.stopPropagation();

    const input = button.parentElement.querySelector(".quantity__input");
    if (!input) return;

    const index = input.dataset.index;
    const currentValue = parseInt(input.value) || 0;
    const step = parseInt(input.step) || 1;
    const min = parseInt(input.dataset.min) || 0;
    const max = parseInt(input.max) || Infinity;
    const available = parseInt(input.dataset.available) || max;

    const plusButton = button.parentElement.querySelector('[name="plus"]');
    if (currentValue >= available) {
      if (plusButton) plusButton.disabled = true;
    } else {
      if (plusButton) plusButton.disabled = false;
    }

    this.updateQuantity(index, currentValue, event);
  }

  updateQuantity(line, quantity, event) {
    const spinner = document.querySelector(`#CartItem-${line} .loading__spinner`);
    if (spinner) spinner.classList.remove("hidden");

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((s) => s.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, {
      ...fetchConfig(),
      body,
    })
      .then((res) => res.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        this.refreshSections(parsedState);
      })
      .catch((e) => console.error("Update quantity error:", e))
      .finally(() => {
        if (spinner) spinner.classList.add("hidden");
      });
  }
}

customElements.define("cart-drawer", CartDrawer);

/** Accessibility helpers */
function trapFocus(container, elementToFocus) {
  const focusableElements = container.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  const firstElement = elementToFocus || focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  container.addEventListener("keydown", (event) => {
    if (event.key === "Tab") {
      if (event.shiftKey && document.activeElement === firstElement) {
        lastElement.focus();
        event.preventDefault();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        firstElement.focus();
        event.preventDefault();
      }
    }
  });

  firstElement?.focus();
}

function removeTrapFocus() {
  document.removeEventListener("keydown", trapFocus);
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;
  const openDetails = event.target.closest("details[open]");
  if (!openDetails) return;
  openDetails.removeAttribute("open");
  const summary = openDetails.querySelector("summary");
  summary.setAttribute("aria-expanded", false);
  summary.focus();
}
